import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

const IFRAME_CSS = `
<style>
  del {
    background: #ffcdd2 !important;
    display: none;
  }

  del p {
    background: #ffcdd2 !important;
  }

  ins {
    background: #d1e3d2 !important;
    display: none;
  }

  ins p {
    background: #d1e3d2 !important;
  }
</style>
`;
const IFRAME_ADD_CSS = `
<style>
   ins {
    display:inline;
  }
</style>
`;
const IFRAME_DEL_CSS = `
<style>
  del {
    display: inline;
  }
</style>

`;

const NO_CONTENT = `
<!doctype html>
<html>
  <head>
    <style>
      body {
        margin: 0;
        height: 100vh;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        color: #5f6368;
        overflow: hidden;
      }
      .container {
        text-align: center;
        padding: 2rem;
        animation: fadeIn 0.3s ease-in-out;
      }
      h2 {
        font-size: 20px;
        font-weight: 500;
        margin: 0 0 8px 0;
        color: #3c4043;
      }
      p {
        font-size: 14px;
        margin: 0;
        line-height: 1.5;
        color: #5f6368;
      }
      .icon {
        font-size: 48px;
        margin-bottom: 16px;
        display: block;
        opacity: 0.8;
      }
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
    </style>
  </head>
  <body>
    <div class="container">
      <span class="icon">📑</span>
      <h2>No Version Selected</h2>
      <p>Select a version to view differences</p>
    </div>
  </body>
</html>
`;

const HTML_WRAPPER_TEMPLATE = `<!DOCTYPE html>
  <html>
  <head>
    <!--IFRAME_STYLES_PLACEHOLDER-->
  </head>
  <body>
    <!--IFRAME_CONTENT_PLACEHOLDER-->
  </body>
  </html>`;

@Component({
  selector: 'app-version-differ',
  templateUrl: './version-differ.component.html',
  styleUrls: ['./version-differ.component.scss', '../../global-workspace-styles.scss'],
  standalone: true,
  imports: []
})
export class VersionDifferComponent {
  safeVersionHtml: SafeHtml | undefined;
  private _versionHtml = '';
  private _showDeleted = false;
  private _showAdded = false;

  @Input({ required: true })
  public set versionHtml(value: string) {
    this._versionHtml = value;
    this.processHtml();
  }

  @Input({ required: true })
  public set showAdded(value: boolean) {
    this._showAdded = value;
    this.processHtml();
  }
  public get showAdded() {
    return this._showAdded;
  }

  @Input({ required: true })
  public set showDeleted(value: boolean) {
    this._showDeleted = value;
    this.processHtml();
  }
  public get showDeleted() {
    return this._showDeleted;
  }

  @ViewChild('versionFrame') iframe: ElementRef<HTMLIFrameElement> | undefined;

  constructor(private sanitizer: DomSanitizer) {}

  iframeLoaded() {
    this.setDocHeight();
    this.removeStartingHtmlText();
  }

  setDocHeight() {
    const iframeEl = this.iframe?.nativeElement;

    if (iframeEl?.contentWindow) {
      // Reset height to allow content to reflow
      iframeEl.style.height = '0px';
      iframeEl.style.height = iframeEl.contentWindow.document.body.scrollHeight + 80 + 'px';
    }
  }

  removeStartingHtmlText() {
    const iframeEl = this.iframe?.nativeElement;
    if (iframeEl?.contentWindow?.document.body.childNodes) {
      const firstChild = iframeEl?.contentWindow?.document.body.childNodes.item(0);
      const currentContent = firstChild.textContent;
      if (currentContent) {
        const isHtmlStarter = currentContent?.trim().startsWith('html');
        if (isHtmlStarter) {
          firstChild.remove();
        }
      }
    }
  }

  replaceEnclosedTextWithNewline(inputString: string): string {
    const regex = /&lt;&lt;&lt;&lt;(.*?)&gt;&gt;&gt;&gt;/g;
    return inputString.replace(regex, '<br>');
  }

  removeBlackFontColor(inputString: string) {
    const match = /color=\\"#000000\\"/g;
    return inputString.replace(match, '');
  }

  private processHtml() {
    const processedHtml = this.getWrappedHtml(this._versionHtml?.length ? this._versionHtml : NO_CONTENT);
    let placeHolderDeletedText = this.replaceEnclosedTextWithNewline(processedHtml);
    placeHolderDeletedText = this.removeBlackFontColor(placeHolderDeletedText);
    this.safeVersionHtml = this.sanitizer.bypassSecurityTrustHtml(placeHolderDeletedText);
  }

  private getWrappedHtml(html: string): string {
    const normalizedHtml = html.trim().toLowerCase();

    if (normalizedHtml.startsWith('<!doctype html>')) {
      return html;
    }

    let wrappedHtml = HTML_WRAPPER_TEMPLATE.replace(
      '<!--IFRAME_STYLES_PLACEHOLDER-->',
      `${IFRAME_CSS}${this._showAdded ? IFRAME_ADD_CSS : ''}${this._showDeleted ? IFRAME_DEL_CSS : ''}`
    );

    wrappedHtml = wrappedHtml.replace('<!--IFRAME_CONTENT_PLACEHOLDER-->', html);

    return wrappedHtml;
  }
}
